import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import WebvonkLoader from '../components/WebvonkLoader';
import { X, Calendar, CheckCircle, Pencil } from 'lucide-react';
import TimePicker from '../components/pickers/TimePicker';
import DatePicker from '../components/pickers/DatePicker';
import { spacing, fontSize, fontWeight, borderRadius } from '../../styles/theme';
import { getKmfcClient } from '../../lib/supabase';
import { usePermissions } from '../hooks/usePermissions';
import { useSession } from '../../contexts/SessionContext';
import { useTheme } from '../../contexts/ThemeContext';
import BackButton from '../components/BackButton';

const DAYS = [
  { key: 1, label: 'Maandag' },
  { key: 2, label: 'Dinsdag' },
  { key: 3, label: 'Woensdag' },
  { key: 4, label: 'Donderdag' },
  { key: 5, label: 'Vrijdag' },
  { key: 6, label: 'Zaterdag' },
  { key: 7, label: 'Zondag' },
];

// ─── Notification toast ───────────────────────────────────────────────────────

const slideDown = keyframes`
  from { opacity: 0; transform: translateX(-50%) translateY(-8px); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0); }
`;

const Toast = styled.div`
  position: fixed;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  padding: 10px 18px;
  border-radius: 100px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  animation: ${slideDown} 0.25s ease;
  background: ${props => props.$type === 'success' ? '#16a34a' : '#dc2626'};
  box-shadow: 0 4px 20px rgba(0,0,0,0.3);
`;

// ─── Layout ───────────────────────────────────────────────────────────────────

const Container = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background};
`;

const Content = styled.div`
  padding: 72px ${spacing.lg} ${spacing.xl};
  max-width: 1100px;
  margin: 0 auto;
  transition: filter 0.25s ease, opacity 0.25s ease;
  filter: ${props => props.$blurred ? 'blur(6px)' : 'none'};
  opacity: ${props => props.$blurred ? 0.4 : 1};
  pointer-events: ${props => props.$blurred ? 'none' : 'auto'};

  @media (max-width: 768px) {
    padding: 72px ${spacing.md} ${spacing.xl};
  }
`;

const DaysGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 20px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: ${fontWeight.bold};
  color: ${props => props.theme.colors.text};
  letter-spacing: -0.02em;
  margin: 0 0 24px;
`;

// ─── Section (like SettingsScreen) ────────────────────────────────────────────

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: ${fontWeight.semibold};
  color: ${props => props.theme.colors.muted};
  text-transform: uppercase;
  letter-spacing: 0.07em;
  margin-bottom: 8px;
  padding-left: 4px;
  opacity: 0.6;
`;

const Card = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid ${props => props.theme.mode === 'light' ? props.theme.colors.border : 'transparent'};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  padding: 13px 14px;
  &:not(:last-child) { border-bottom: 1px solid ${props => props.theme.mode === 'light' ? props.theme.colors.border : 'rgba(255,255,255,0.05)'}; }
`;

const RowLabel = styled.div`
  flex: 1;
  font-size: 15px;
  font-weight: ${fontWeight.medium};
  color: ${props => props.$color || props.theme.colors.text};
  letter-spacing: -0.01em;
`;

const RowValue = styled.div`
  font-size: 14px;
  color: ${props => props.theme.colors.muted};
  opacity: 0.5;
  margin-right: 8px;
`;

// ─── Toggle ───────────────────────────────────────────────────────────────────

const ToggleTrack = styled.button`
  width: 48px;
  height: 28px;
  border-radius: 14px;
  border: none;
  cursor: pointer;
  position: relative;
  transition: background 0.25s ease;
  flex-shrink: 0;
  background: ${props => props.$on
    ? (props.$color === 'red' ? '#ef4444' : '#22c55e')
    : props.theme.mode === 'light' ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.12)'};

  &::after {
    content: '';
    position: absolute;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #fff;
    top: 3px;
    left: ${props => props.$on ? '23px' : '3px'};
    transition: left 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    box-shadow: 0 2px 6px rgba(0,0,0,0.25);
  }
`;

// ─── Time chip ────────────────────────────────────────────────────────────────

const TimeChip = styled.button`
  padding: 5px 11px;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  font-size: 14px;
  font-weight: ${fontWeight.medium};
  font-family: 'SF Mono', 'Monaco', monospace;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${props => props.theme.colors.border};
    border-color: ${props => props.theme.colors.border};
  }
`;

// ─── Modals ───────────────────────────────────────────────────────────────────

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.3);
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  display: grid;
  place-items: center;
  z-index: 99999;
  padding: ${spacing.lg};
  overflow-y: auto;
  overscroll-behavior: contain;
`;

const ModalContent = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: 18px;
  width: 95%;
  max-width: 980px;
  max-height: 88vh;
  overflow-y: auto;
  border: 1px solid ${props => props.theme.colors.border};
  box-shadow: 0 32px 80px rgba(0,0,0,0.4);

  @keyframes slideUp {
    from { transform: translateY(16px); opacity: 0; }
    to   { transform: translateY(0); opacity: 1; }
  }
  animation: slideUp 0.25s cubic-bezier(0.25,0.46,0.45,0.94);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 18px 14px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const ModalTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin: 0;
  letter-spacing: -0.01em;
`;

const CloseButton = styled.button`
  background: ${props => props.theme.colors.border};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 50%;
  width: 28px;
  height: 28px;
  cursor: pointer;
  color: ${props => props.theme.colors.muted};
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: ${props => props.theme.colors.border}; color: ${props => props.theme.colors.text}; }
`;

const ModalBody = styled.div`
  padding: 18px;
`;

const TimeInput = styled.input`
  width: 100%;
  padding: 14px;
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: 12px;
  font-size: 28px;
  color: ${props => props.theme.colors.text};
  background: ${props => props.theme.colors.background};
  font-family: 'SF Mono', 'Monaco', monospace;
  text-align: center;
  margin-bottom: 16px;
  letter-spacing: 3px;
  font-weight: 600;
  box-sizing: border-box;
  color-scheme: ${props => props.theme.mode};

  &:focus {
    outline: none;
    border-color: #f97316;
    box-shadow: 0 0 0 3px rgba(249,115,22,0.15);
  }

  &::placeholder { color: ${props => props.theme.colors.muted}; opacity: 0.4; }
`;

const QuickTimeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
  margin-bottom: 16px;
`;

const QuickTimeBtn = styled.button`
  padding: 8px 4px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 9px;
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.muted};
  font-size: 13px;
  font-family: 'SF Mono', 'Monaco', monospace;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(249,115,22,0.15);
    border-color: rgba(249,115,22,0.4);
    color: #f97316;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 8px;
`;

const PrimaryBtn = styled.button`
  flex: 1;
  padding: 12px;
  background: #f97316;
  color: #fff;
  border: none;
  border-radius: 100px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease;
  &:hover { filter: brightness(0.9); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const SecondaryBtn = styled.button`
  flex: 1;
  padding: 12px;
  background: transparent;
  color: ${props => props.theme.colors.muted};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 100px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  &:hover { color: ${props => props.theme.colors.text}; }
`;

const ReasonTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 11px 13px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 12px;
  font-size: 14px;
  color: ${props => props.theme.colors.text};
  background: ${props => props.theme.colors.surface};
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;
  margin-bottom: 14px;
  color-scheme: ${props => props.theme.mode};

  &:focus {
    outline: none;
    border-color: #f97316;
    box-shadow: 0 0 0 3px rgba(249,115,22,0.1);
  }

  &::placeholder { color: ${props => props.theme.colors.muted}; opacity: 0.5; }
`;

const DateInput = styled.input`
  width: 100%;
  padding: 10px 13px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 10px;
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  font-size: 14px;
  box-sizing: border-box;
  color-scheme: ${props => props.theme.mode};

  &:focus { outline: none; border-color: #f97316; }
`;

const TemplateChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
`;

const TemplateChip = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 100px;
  border: 1px solid rgba(255,255,255,0.08);
  background: rgba(255,255,255,0.03);
  color: ${props => props.theme.colors.muted};
  font-size: 12px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    background: rgba(255,255,255,0.07);
    border-color: rgba(255,255,255,0.15);
    color: ${props => props.theme.colors.text};
  }
`;

const TemplateDot = styled.div`
  width: 6px; height: 6px;
  border-radius: 50%;
  background: ${p => p.$color};
  flex-shrink: 0;
`;

const SegmentControl = styled.div`
  display: flex;
  background: ${props => props.theme.colors.border};
  border-radius: 10px;
  padding: 3px;
  margin-bottom: 16px;
  gap: 3px;
`;

const Segment = styled.button`
  flex: 1;
  padding: 7px 4px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  background: ${props => props.$active ? '#f97316' : 'transparent'};
  color: ${props => props.$active ? '#fff' : props.theme.colors.muted};
`;

// ─── Component ────────────────────────────────────────────────────────────────

export default function OpeningHoursScreen() {
  const { colors, themeMode } = useTheme();
  const isLight = themeMode === 'light';
  const location = useLocation();
  const didAutoOpen = useRef(false);
  const [openingHours, setOpeningHours] = useState({});
  const [loading, setLoading] = useState(true);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedTimeType, setSelectedTimeType] = useState(null);
  const [timeInputValue, setTimeInputValue] = useState('');
  const [originalTimeValue, setOriginalTimeValue] = useState('');
  const [toast, setToast] = useState(null);
  const [sluitingen, setSluitingen] = useState([]);
  const [loadingSluitingen, setLoadingSluitingen] = useState(false);
  const [showAddSluitingModal, setShowAddSluitingModal] = useState(false);
  const [newSluitType, setNewSluitType] = useState('wholeday');
  const [newSluitStartDate, setNewSluitStartDate] = useState('');
  const [newSluitStartTime, setNewSluitStartTime] = useState('12:00');
  const [newSluitEndDate, setNewSluitEndDate] = useState('');
  const [newSluitEndTime, setNewSluitEndTime] = useState('22:00');
  const [newSluitTitle, setNewSluitTitle] = useState('');
  const [newSluitReason, setNewSluitReason] = useState('');
  const [newSluitCanCall, setNewSluitCanCall] = useState(true);
  const [editingSluiting, setEditingSluiting] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmHours, setConfirmHours] = useState(null); // { action: fn, label: string }

  useEffect(() => {
    loadOpeningHours();
    loadSluitingen();
  }, []);

  useEffect(() => {
    const anyOpen = showTimePicker || showAddSluitingModal;
    if (anyOpen) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      return () => {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [showTimePicker, showAddSluitingModal]);

  useEffect(() => {
    if (!loading && !didAutoOpen.current) {
      const params = new URLSearchParams(location.search);
      if (params.get('newSluiting') === '1') {
        didAutoOpen.current = true;
        const algemeen = SLUITING_TEMPLATES[0];
        setEditingSluiting(null);
        setNewSluitType(algemeen.type);
        setNewSluitTitle(algemeen.title);
        setNewSluitReason(algemeen.reason);
        setNewSluitCanCall(algemeen.canCall);
        setNewSluitStartDate(algemeen.startDate());
        setNewSluitStartTime(algemeen.startTime);
        setNewSluitEndDate(algemeen.endDate());
        setNewSluitEndTime(algemeen.endTime);
        setShowAddSluitingModal(true);
      }
    }
  }, [loading, location.search]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  const loadOpeningHours = async () => {
    try {
      const kmfcClient = getKmfcClient();
      if (!kmfcClient) return;
      const { data, error } = await kmfcClient
        .from('opening_hours')
        .select('id, weekday, open_time, close_time, is_active')
        .order('weekday', { ascending: true });
      if (error) throw error;
      const hours = {};
      DAYS.forEach(day => {
        const d = data?.find(d => d.weekday === day.key);
        hours[day.key] = {
          id: d?.id || null,
          isOpen: d?.is_active || false,
          openTime: d?.open_time || '09:00',
          closeTime: d?.close_time || '17:00',
        };
      });
      setOpeningHours(hours);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadSluitingen = async () => {
    const client = getKmfcClient();
    if (!client) return;
    setLoadingSluitingen(true);
    try {
      const { data, error } = await client.from('sluitingen').select('*').gte('end_datetime', new Date().toISOString()).order('start_datetime', { ascending: true });
      if (error) throw error;
      setSluitingen(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingSluitingen(false);
    }
  };

  const toggleDayOpen = async (dayKey, isOpen) => {
    const kmfcClient = getKmfcClient();
    if (!kmfcClient) return;
    const dayData = openingHours[dayKey];
    try {
      if (dayData?.id) {
        const { error } = await kmfcClient.from('opening_hours').update({ is_active: isOpen }).eq('id', dayData.id);
        if (error) throw error;
      } else {
        const { data: rec, error } = await kmfcClient.from('opening_hours').insert({ weekday: dayKey, is_active: isOpen, open_time: '09:00', close_time: '17:00' }).select().single();
        if (error) throw error;
        setOpeningHours(prev => ({ ...prev, [dayKey]: { id: rec.id, isOpen: rec.is_active, openTime: rec.open_time, closeTime: rec.close_time } }));
        return;
      }
      setOpeningHours(prev => ({ ...prev, [dayKey]: { ...prev[dayKey], isOpen } }));
    } catch (e) {
      console.error(e);
    }
  };

  const updateTime = async (dayKey, timeType, time) => {
    const kmfcClient = getKmfcClient();
    if (!kmfcClient) return;
    const dayData = openingHours[dayKey];
    const column = timeType === 'open' ? 'open_time' : 'close_time';
    try {
      if (dayData?.id) {
        const { error } = await kmfcClient.from('opening_hours').update({ [column]: time }).eq('id', dayData.id);
        if (error) throw error;
      } else {
        const { data: rec, error } = await kmfcClient.from('opening_hours').insert({ weekday: dayKey, is_active: true, open_time: timeType === 'open' ? time : '09:00', close_time: timeType === 'close' ? time : '17:00' }).select().single();
        if (error) throw error;
        setOpeningHours(prev => ({ ...prev, [dayKey]: { id: rec.id, isOpen: rec.is_active, openTime: rec.open_time, closeTime: rec.close_time } }));
        return;
      }
      setOpeningHours(prev => ({ ...prev, [dayKey]: { ...prev[dayKey], [`${timeType}Time`]: time } }));
    } catch (e) {
      console.error(e);
    }
  };

  const openTimePicker = (dayKey, timeType) => {
    setSelectedDay(dayKey);
    setSelectedTimeType(timeType);
    const d = openingHours[dayKey] || { openTime: '09:00', closeTime: '17:00' };
    const val = timeType === 'open' ? d.openTime : d.closeTime;
    setTimeInputValue(val);
    setOriginalTimeValue(val);
    setShowTimePicker(true);
  };

  const timeChanged = timeInputValue !== originalTimeValue;

  const handleTimeSave = () => {
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/;
    if (!timeRegex.test(timeInputValue)) {
      showToast('Voer een geldige tijd in (bijv. 09:00)', 'error');
      return;
    }
    if (selectedDay !== null && selectedTimeType && timeChanged) {
      const d = openingHours[selectedDay] || { openTime: '09:00', closeTime: '17:00' };
      const [oh, om] = (selectedTimeType === 'open' ? timeInputValue : d.openTime).split(':').map(Number);
      const [ch, cm] = (selectedTimeType === 'close' ? timeInputValue : d.closeTime).split(':').map(Number);
      if (selectedTimeType === 'open' && oh * 60 + om >= ch * 60 + cm) {
        showToast('Openingstijd moet vóór sluitijd zijn', 'error'); return;
      }
      if (selectedTimeType === 'close' && ch * 60 + cm <= oh * 60 + om) {
        showToast('Sluitijd moet na openingstijd zijn', 'error'); return;
      }
      const dayLabel = DAYS.find(d => d.key === selectedDay)?.label;
      const typeLabel = selectedTimeType === 'open' ? 'openingstijd' : 'sluitingstijd';
      setConfirmHours({
        label: `Wil je de ${typeLabel} van ${dayLabel} wijzigen naar ${timeInputValue}?`,
        action: () => { updateTime(selectedDay, selectedTimeType, timeInputValue); setShowTimePicker(false); setSelectedDay(null); },
      });
      return;
    }
    setShowTimePicker(false);
    setSelectedDay(null);
    setSelectedTimeType(null);
    setTimeInputValue('');
  };

  const handleTimeInputChange = (e) => {
    let v = e.target.value.replace(/[^0-9:]/g, '');
    if (v.length === 2 && !timeInputValue.includes(':') && !v.includes(':')) v += ':';
    if (v.length > 5) v = v.slice(0, 5);
    setTimeInputValue(v);
  };

  const openAddModal = () => {
    setEditingSluiting(null);
    const algemeen = SLUITING_TEMPLATES[0];
    setNewSluitType(algemeen.type);
    setNewSluitTitle(algemeen.title);
    setNewSluitReason(algemeen.reason);
    setNewSluitCanCall(algemeen.canCall);
    setNewSluitStartDate(algemeen.startDate());
    setNewSluitStartTime(algemeen.startTime);
    setNewSluitEndDate(algemeen.endDate());
    setNewSluitEndTime(algemeen.endTime);
    setShowAddSluitingModal(true);
  };

  const openEditModal = (s) => {
    setEditingSluiting(s);
    setNewSluitTitle(s.title || '');
    setNewSluitReason(s.reason || '');
    setNewSluitType(s.is_range ? 'period' : s.full_day ? 'wholeday' : 'partialday');
    // Parse als UTC, toon in Amsterdam-tijd in het formulier
    const toAmsStr = (isoStr, type) => {
      const d = parseUTC(isoStr);
      if (!d) return '';
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Amsterdam',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false,
      }).formatToParts(d);
      const p = Object.fromEntries(parts.map(x => [x.type, x.value]));
      return type === 'date' ? `${p.year}-${p.month}-${p.day}` : `${p.hour}:${p.minute}`;
    };
    setNewSluitStartDate(toAmsStr(s.start_datetime, 'date'));
    setNewSluitStartTime(toAmsStr(s.start_datetime, 'time'));
    setNewSluitEndDate(toAmsStr(s.end_datetime, 'date'));
    setNewSluitEndTime(toAmsStr(s.end_datetime, 'time'));
    setNewSluitCanCall(s.can_call !== false);
    setShowAddSluitingModal(true);
  };

  const saveSluiting = async () => {
    const client = getKmfcClient();
    if (!client || !newSluitTitle.trim() || !newSluitStartDate) return;

    // Interpreteer datum+tijd als Amsterdam-tijd en converteer naar UTC
    const toISO = (dateStr, timeStr) => {
      const t = timeStr.length === 5 ? `${timeStr}:00` : timeStr;
      const ref = new Date(`${dateStr}T${t}Z`);
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Amsterdam',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false,
      }).formatToParts(ref);
      const p = Object.fromEntries(parts.map(x => [x.type, x.value]));
      const amsAsUtc = new Date(`${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:${p.second}Z`);
      const offsetMs = amsAsUtc - ref;
      return new Date(ref.getTime() - offsetMs).toISOString();
    };
    const startDt = newSluitType === 'wholeday' ? toISO(newSluitStartDate, '00:00:00') : toISO(newSluitStartDate, `${newSluitStartTime}:00`);
    const endDt = newSluitType === 'wholeday' ? toISO(newSluitStartDate, '23:59:59') : newSluitType === 'partialday' ? toISO(newSluitStartDate, `${newSluitEndTime}:00`) : toISO(newSluitEndDate, `${newSluitEndTime}:00`);
    const payload = { title: newSluitTitle.trim(), start_datetime: startDt, end_datetime: endDt, reason: newSluitReason.trim(), full_day: newSluitType === 'wholeday', is_range: newSluitType === 'period', can_call: newSluitCanCall };
    try {
      if (editingSluiting) {
        const { error } = await client.from('sluitingen').update(payload).eq('id', editingSluiting.id);
        if (error) throw error;
        showToast('Sluiting bijgewerkt');
      } else {
        const { error } = await client.from('sluitingen').insert(payload);
        if (error) throw error;
        showToast('Sluiting toegevoegd');
      }
      setShowAddSluitingModal(false);
      setEditingSluiting(null);
      await loadSluitingen();
    } catch (e) { console.error(e); }
  };

  const deleteSluiting = async (id) => {
    const client = getKmfcClient();
    if (!client) return;
    try {
      const { error } = await client.from('sluitingen').delete().eq('id', id);
      if (error) throw error;
      setSluitingen(prev => prev.filter(s => s.id !== id));
    } catch (e) { console.error(e); }
  };

  // Parse UTC string → Date (exact zoals Swift's parseUTC)
  const parseUTC = (str) => {
    if (!str) return null;
    // ISO8601 met timezone info → direct parsen
    if (str.includes('T') || str.includes('+') || str.endsWith('Z')) return new Date(str);
    // "yyyy-MM-dd HH:mm:ss" → forceer UTC
    return new Date(str.replace(' ', 'T') + 'Z');
  };

  // Datum in Amsterdam-tijd tonen (exact zoals Swift's formattedDateRange)
  const AMS = 'Europe/Amsterdam';
  const formatSluitingDate = (s) => {
    const start = parseUTC(s.start_datetime);
    const end   = parseUTC(s.end_datetime);
    if (!start) return s.start_datetime;

    const dateFmt = (d, opts) => d.toLocaleDateString('nl-NL', { timeZone: AMS, ...opts });
    const timeFmt = (d) => d.toLocaleTimeString('nl-NL', { timeZone: AMS, hour: '2-digit', minute: '2-digit' });

    if (s.full_day && !s.is_range) {
      // "woensdag 23 juli" — exact Swift formaat
      return dateFmt(start, { weekday: 'long', day: 'numeric', month: 'long' });
    }
    if (s.is_range && end) {
      // "23 jul – 25 jul"
      return `${dateFmt(start, { day: 'numeric', month: 'short' })} – ${dateFmt(end, { day: 'numeric', month: 'short' })}`;
    }
    // Gedeelte van dag: "23 jul, 12:00–22:00"
    const timeRange = end ? `${timeFmt(start)}–${timeFmt(end)}` : timeFmt(start);
    return `${dateFmt(start, { day: 'numeric', month: 'short' })}, ${timeRange}`;
  };

  if (loading) return <WebvonkLoader fullscreen text="Openingstijden worden geladen..." />;

  const today = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  };

  const addDays = (n) => {
    const d = new Date(); d.setDate(d.getDate() + n);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  };

  const SLUITING_TEMPLATES = [
    {
      name: 'Algemeen',
      dot: '#f97316',
      type: 'wholeday',
      title: '',
      reason: `Beste klant! 🎉\n\nVandaag zijn we gesloten vanwege [reden]. Maar geen zorgen — morgen staan we weer voor u klaar met verse bestellingen!\n\nTot dan! 👋\n\n— Team KMFC Shop`,
      canCall: false,
      startDate: () => today(), startTime: '00:00', endDate: () => today(), endTime: '23:59',
    },
    {
      name: 'Onderhoud',
      dot: '#f59e0b',
      type: 'wholeday',
      title: 'Website onderhoud',
      reason: `Beste klant! 👋\n\nOnze website is even offline voor onderhoud 🚧 We zijn hard bezig om alles zo snel mogelijk weer voor u klaar te zetten!\n\nOnze excuses voor het ongemak.\n\n— Team KMFC Shop`,
      canCall: true,
      startDate: () => today(), startTime: '00:00', endDate: () => today(), endTime: '23:59',
    },
    {
      name: 'Vakantie',
      dot: '#8b5cf6',
      type: 'period',
      title: 'Vakantie',
      reason: `Beste klant! 🌴\n\nWe genieten even van vakantie! Geen zorgen — we zijn er snel weer voor u.\n\nTot dan, geniet ook van het mooie weer! ☀️\n\n— Team KMFC Shop`,
      canCall: false,
      startDate: () => addDays(1), startTime: '00:00', endDate: () => addDays(7), endTime: '23:59',
    },
    {
      name: 'Feestdag',
      dot: '#3b82f6',
      type: 'wholeday',
      title: 'Feestdag',
      reason: `Beste klant! 🎊\n\nVanwege de feestdag zijn we vandaag gesloten 😊\n\nBinnenkort zijn we er weer voor u! Tot dan 👋\n\n— Team KMFC Shop`,
      canCall: false,
      startDate: () => today(), startTime: '00:00', endDate: () => today(), endTime: '23:59',
    },
    {
      name: 'Uitverkocht',
      dot: '#ef4444',
      type: 'wholeday',
      title: 'Uitverkocht',
      reason: `Beste klant!\n\nDoor extreme drukte zijn we grotendeels uitverkocht... U kunt ons bellen om te bestellen.\nWe zijn morgen weer gewoon open voor u. Bedankt voor uw begrip! 🙏\n\n— Team KMFC Shop`,
      canCall: true,
      startDate: () => today(), startTime: '00:00', endDate: () => today(), endTime: '23:59',
    },
    {
      name: 'Te druk',
      dot: '#f97316',
      type: 'partialday',
      title: 'Tijdelijk gesloten',
      reason: `Beste klant! 🙏\n\nVanwege grote drukte is de website tijdelijk gesloten. We doen ons best om zo snel mogelijk weer online bestellingen aan te nemen!\n\nBedankt voor uw geduld.\n\n— Team KMFC Shop`,
      canCall: true,
      startDate: () => today(), startTime: '17:00', endDate: () => today(), endTime: '21:00',
    },
    {
      name: 'Slecht weer',
      dot: '#64748b',
      type: 'wholeday',
      title: 'Gesloten wegens weer',
      reason: `Beste klant! 🌧️\n\nVanwege het slechte weer zijn we vandaag helaas gesloten. De veiligheid van ons team en onze bezorgers staat voorop!\n\nWe zijn morgen weer gewoon voor u open. Tot dan! 👋\n\n— Team KMFC Shop`,
      canCall: false,
      startDate: () => today(), startTime: '00:00', endDate: () => today(), endTime: '23:59',
    },
    {
      name: 'Warm weer',
      dot: '#f59e0b',
      type: 'wholeday',
      title: 'Gesloten wegens hitte',
      reason: `Beste klant! ☀️\n\nVanwege de extreme hitte sluiten we vandaag de deuren. Excuses voor het ongemak!\n\nMorgen staan we weer voor u klaar. 🙏\n\n— Team KMFC Shop`,
      canCall: false,
      startDate: () => today(), startTime: '00:00', endDate: () => today(), endTime: '23:59',
    },
    {
      name: 'Ziekte',
      dot: '#ec4899',
      type: 'wholeday',
      title: 'Gesloten wegens ziekte',
      reason: `Beste klant! 🤒\n\nEen deel van ons team is helaas ziek gevallen, waardoor we vandaag de deuren moeten sluiten. Onze excuses voor het ongemak.\n\nWe hopen snel weer voor u open te zijn. Bedankt voor uw begrip! 🙏\n\n— Team KMFC Shop`,
      canCall: false,
      startDate: () => today(), startTime: '00:00', endDate: () => today(), endTime: '23:59',
    },
    {
      name: 'Evenement',
      dot: '#22c55e',
      type: 'wholeday',
      title: 'Gesloten wegens event',
      reason: `Beste klant! 🎉\n\nVandaag zijn we gesloten vanwege een evenement. Maar geen zorgen — morgen staan we weer voor u klaar met verse bestellingen!\n\nTot dan! 👋\n\n— Team KMFC Shop`,
      canCall: false,
      startDate: () => today(), startTime: '00:00', endDate: () => today(), endTime: '23:59',
    },
    {
      name: 'Technisch',
      dot: '#6366f1',
      type: 'wholeday',
      title: 'Technische storing',
      reason: `Beste klant! 🔧\n\nWe ondervinden momenteel een technische storing waardoor we helaas geen online bestellingen kunnen aannemen. We zijn er hard mee bezig!\n\nZodra alles weer werkt staan we voor u klaar. Onze excuses voor het ongemak!\n\n— Team KMFC Shop`,
      canCall: true,
      startDate: () => today(), startTime: '00:00', endDate: () => today(), endTime: '23:59',
    },
    {
      name: 'Privé',
      dot: '#f43f5e',
      type: 'wholeday',
      title: 'Gesloten',
      reason: `Beste klant! 👋\n\nVandaag zijn we gesloten vanwege een privéaangelegenheid. We zijn morgen weer gewoon voor u open!\n\nBedankt voor uw begrip. 🙏\n\n— Team KMFC Shop`,
      canCall: false,
      startDate: () => today(), startTime: '00:00', endDate: () => today(), endTime: '23:59',
    },
    {
      name: 'Personeelstekort',
      dot: '#10b981',
      type: 'wholeday',
      title: 'Gesloten wegens personeelstekort',
      reason: `Beste klant! 👥\n\nVandaag hebben we helaas te weinig personeel beschikbaar om veilig en kwalitatief te kunnen werken. Daardoor zijn we vandaag gesloten.\n\nMorgen zijn we er weer gewoon voor u! Bedankt voor uw begrip. 🙏\n\n— Team KMFC Shop`,
      canCall: false,
      startDate: () => today(), startTime: '00:00', endDate: () => today(), endTime: '23:59',
    },
    {
      name: 'Stroom',
      dot: '#eab308',
      type: 'wholeday',
      title: 'Stroomstoring',
      reason: `Beste klant! ⚡\n\nWe hebben momenteel te maken met een stroomstoring, waardoor we helaas geen bestellingen kunnen verwerken. We hopen dit zo snel mogelijk opgelost te hebben!\n\nExcuses voor het ongemak. 🙏\n\n— Team KMFC Shop`,
      canCall: true,
      startDate: () => today(), startTime: '00:00', endDate: () => today(), endTime: '23:59',
    },
    {
      name: 'Nieuwjaar',
      dot: '#a855f7',
      type: 'wholeday',
      title: 'Oud & nieuw',
      reason: `Beste klant! 🎆\n\nVanwege oud & nieuw zijn we vandaag gesloten. We wensen u alvast een fantastisch nieuw jaar toe!\n\nVanaf morgen staan we weer voor u klaar. Gelukkig nieuwjaar! 🥂\n\n— Team KMFC Shop`,
      canCall: false,
      startDate: () => today(), startTime: '00:00', endDate: () => today(), endTime: '23:59',
    },
  ];

  const applySluitingTemplate = (tpl) => {
    setEditingSluiting(null);
    setNewSluitType(tpl.type);
    setNewSluitTitle(tpl.title);
    setNewSluitReason(tpl.reason);
    setNewSluitCanCall(tpl.canCall ?? true);
    setNewSluitStartDate(tpl.startDate());
    setNewSluitStartTime(tpl.startTime);
    setNewSluitEndDate(tpl.endDate());
    setNewSluitEndTime(tpl.endTime);
    setShowAddSluitingModal(true);
  };

  const activeSluitingen = sluitingen.filter(s => new Date() >= new Date(s.start_datetime) && new Date() <= new Date(s.end_datetime));
  const plannedSluitingen = sluitingen.filter(s => new Date() < new Date(s.start_datetime));
  const visibleSluitingen = [...activeSluitingen, ...plannedSluitingen];
  const anyModal = showTimePicker || showAddSluitingModal || !!confirmDelete;

  return (
    <Container>


      {toast && <Toast $type={toast.type}>{toast.msg}</Toast>}

      <Content $blurred={anyModal}>
        <PageTitle>Openingstijden</PageTitle>

        {/* Per-dag cards */}
        <DaysGrid>
        {DAYS.map(day => {
          const d = openingHours[day.key] || { isOpen: false, openTime: '09:00', closeTime: '17:00' };
          return (
            <Section key={day.key}>
              <SectionLabel>{day.label}</SectionLabel>
              <Card>
                <Row>
                  <RowLabel $color={d.isOpen ? '#22c55e' : '#ef4444'}>
                    {d.isOpen ? 'Open' : 'Gesloten'}
                  </RowLabel>
                  <ToggleTrack
                    $on={d.isOpen}
                    $color={d.isOpen ? 'green' : 'red'}
                    onClick={() => setConfirmHours({
                      label: `Wil je ${day.label} markeren als ${d.isOpen ? 'gesloten' : 'open'}?`,
                      action: () => toggleDayOpen(day.key, !d.isOpen),
                    })}
                  />
                </Row>
                {d.isOpen && (
                  <>
                    <Row>
                      <RowLabel>Openingstijd</RowLabel>
                      <TimeChip onClick={() => openTimePicker(day.key, 'open')}>{d.openTime}</TimeChip>
                    </Row>
                    <Row>
                      <RowLabel>Sluitingstijd</RowLabel>
                      <TimeChip onClick={() => openTimePicker(day.key, 'close')}>{d.closeTime}</TimeChip>
                    </Row>
                  </>
                )}
              </Card>
            </Section>
          );
        })}
        </DaysGrid>

        {/* Sluitingen */}
        <Section>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, paddingLeft: 4 }}>
            <SectionLabel style={{ margin: 0 }}>Gesloten met reden</SectionLabel>
            <button
              onClick={openAddModal}
              style={{ background: 'rgba(249,115,22,0.12)', border: '1px solid rgba(249,115,22,0.25)', borderRadius: 100, padding: '4px 12px', color: '#f97316', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
            >
              + Toevoegen
            </button>
          </div>

          {activeSluitingen.length > 0 && (
            <>
              <SectionLabel style={{ color: '#ef4444', opacity: 1, marginBottom: 6 }}>Nu actief</SectionLabel>
              <Card style={{ marginBottom: 12, border: '1px solid rgba(239,68,68,0.25)' }}>
                {activeSluitingen.map(s => (
                  <Row key={s.id}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', marginRight: 12, flexShrink: 0, boxShadow: '0 0 6px #ef4444' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 15, fontWeight: 600, color: colors.text, marginBottom: 2 }}>{s.title || s.reason}</div>
                      <div style={{ fontSize: 13, color: colors.muted, opacity: 0.6 }}>{formatSluitingDate(s)}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => openEditModal(s)} style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, cursor: 'pointer', color: colors.muted, padding: '5px 7px', display: 'flex', alignItems: 'center' }}>
                        <Pencil size={13} />
                      </button>
                      <button onClick={() => setConfirmDelete(s)} style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, cursor: 'pointer', color: '#ef4444', padding: '5px 7px', display: 'flex', alignItems: 'center' }}>
                        <X size={14} />
                      </button>
                    </div>
                  </Row>
                ))}
              </Card>
            </>
          )}

          {plannedSluitingen.length > 0 && (
            <>
              {activeSluitingen.length > 0 && <SectionLabel style={{ marginBottom: 6 }}>Gepland</SectionLabel>}
              <Card>
                {plannedSluitingen.map(s => (
                  <Row key={s.id}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: colors.muted, opacity: 0.4, marginRight: 12, flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 15, fontWeight: 600, color: colors.text, marginBottom: 2 }}>{s.title || s.reason}</div>
                      <div style={{ fontSize: 13, color: colors.muted, opacity: 0.6 }}>{formatSluitingDate(s)}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => openEditModal(s)} style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, cursor: 'pointer', color: colors.muted, padding: '5px 7px', display: 'flex', alignItems: 'center' }}>
                        <Pencil size={13} />
                      </button>
                      <button onClick={() => setConfirmDelete(s)} style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, cursor: 'pointer', color: colors.muted, padding: '5px 7px', display: 'flex', alignItems: 'center' }}>
                        <X size={14} />
                      </button>
                    </div>
                  </Row>
                ))}
              </Card>
            </>
          )}

          {visibleSluitingen.length === 0 && !loadingSluitingen && (
            <Card>
              <Row>
                <CheckCircle size={15} color={colors.muted} style={{ marginRight: 10, flexShrink: 0, opacity: 0.4 }} />
                <RowLabel style={{ color: colors.muted, fontSize: 14, opacity: 0.5 }}>Geen sluitingen gepland</RowLabel>
              </Row>
            </Card>
          )}
        </Section>
      </Content>

      {/* Confirm hours change dialog */}
      {confirmHours && (
        <ModalOverlay data-modal="true" onClick={() => setConfirmHours(null)} style={{ zIndex: 200000 }}>
          <ModalContent onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <ModalHeader>
              <ModalTitle>Openingstijden aanpassen</ModalTitle>
              <CloseButton onClick={() => setConfirmHours(null)}><X size={16} /></CloseButton>
            </ModalHeader>
            <ModalBody>
              <p style={{ margin: '0 0 20px', color: colors.text, fontSize: 14, lineHeight: 1.5 }}>
                {confirmHours.label}
              </p>
              <ModalActions>
                <SecondaryBtn onClick={() => setConfirmHours(null)}>Annuleren</SecondaryBtn>
                <PrimaryBtn onClick={() => { confirmHours.action(); setConfirmHours(null); }}>Doorgaan</PrimaryBtn>
              </ModalActions>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Time Picker Modal */}
      {showTimePicker && (
        <ModalOverlay data-modal="true" onClick={() => setShowTimePicker(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {selectedTimeType === 'open' ? 'Openingstijd' : 'Sluitingstijd'} — {selectedDay !== null && DAYS.find(d => d.key === selectedDay)?.label}
              </ModalTitle>
              <CloseButton onClick={() => setShowTimePicker(false)}><X size={16} /></CloseButton>
            </ModalHeader>
            <ModalBody>
              <TimePicker value={timeInputValue} onChange={setTimeInputValue} minuteStep={5} />
              <div style={{ marginTop: 16 }}>
                <ModalActions>
                  <SecondaryBtn onClick={() => setShowTimePicker(false)}>Annuleren</SecondaryBtn>
                  <PrimaryBtn onClick={handleTimeSave} disabled={!timeChanged}>Opslaan</PrimaryBtn>
                </ModalActions>
              </div>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Add Sluiting Modal */}
      {showAddSluitingModal && (
        <ModalOverlay data-modal="true" onClick={() => setShowAddSluitingModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{editingSluiting ? 'Sluiting bewerken' : 'Sluiting toevoegen'}</ModalTitle>
              <CloseButton onClick={() => setShowAddSluitingModal(false)}><X size={16} /></CloseButton>
            </ModalHeader>
            <ModalBody>
              {/* Twee kolommen */}
              <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>

                {/* Linker kolom: datum & type */}
                <div style={{ flex: '0 0 300px', borderRight: `1px solid ${colors.border}`, paddingRight: 24 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: colors.muted, marginBottom: 12 }}>Periode</div>
                  <SegmentControl style={{ marginBottom: 16 }}>
                    {[['wholeday','Hele dag'],['partialday','Deel dag'],['period','Periode']].map(([val, lbl]) => (
                      <Segment key={val} $active={newSluitType === val} onClick={() => setNewSluitType(val)}>{lbl}</Segment>
                    ))}
                  </SegmentControl>

                  {newSluitType === 'wholeday' && (
                    <>
                      <div style={{ fontSize: 12, color: colors.muted, marginBottom: 6 }}>Datum</div>
                      <DatePicker value={newSluitStartDate} onChange={setNewSluitStartDate} />
                    </>
                  )}

                  {newSluitType === 'partialday' && (
                    <>
                      <div style={{ fontSize: 12, color: colors.muted, marginBottom: 6 }}>Datum</div>
                      <DatePicker value={newSluitStartDate} onChange={setNewSluitStartDate} />
                      <div style={{ display: 'flex', gap: 12, marginTop: 14 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, color: colors.muted, marginBottom: 4 }}>Begin</div>
                          <TimePicker value={newSluitStartTime} onChange={setNewSluitStartTime} minuteStep={5} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, color: colors.muted, marginBottom: 4 }}>Eind</div>
                          <TimePicker value={newSluitEndTime} onChange={setNewSluitEndTime} minuteStep={5} />
                        </div>
                      </div>
                    </>
                  )}

                  {newSluitType === 'period' && (
                    <>
                      <div style={{ fontSize: 12, color: colors.muted, marginBottom: 6 }}>Begindatum</div>
                      <DatePicker value={newSluitStartDate} onChange={setNewSluitStartDate} />
                      <div style={{ marginTop: 10, marginBottom: 4, fontSize: 12, color: colors.muted }}>Begintijd</div>
                      <TimePicker value={newSluitStartTime} onChange={setNewSluitStartTime} minuteStep={5} />
                      <div style={{ height: 1, background: colors.border, margin: '16px 0' }} />
                      <div style={{ fontSize: 12, color: colors.muted, marginBottom: 6 }}>Einddatum</div>
                      <DatePicker value={newSluitEndDate} onChange={setNewSluitEndDate} />
                      <div style={{ marginTop: 10, marginBottom: 4, fontSize: 12, color: colors.muted }}>Eindtijd</div>
                      <TimePicker value={newSluitEndTime} onChange={setNewSluitEndTime} minuteStep={5} />
                    </>
                  )}
                </div>

                {/* Rechter kolom: titel, bericht, toggle */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: colors.muted, marginBottom: 10 }}>Inhoud</div>
                  <TemplateChips style={{ marginBottom: 14 }}>
                    {SLUITING_TEMPLATES.map(tpl => (
                      <TemplateChip key={tpl.name} onClick={() => applySluitingTemplate(tpl)}>
                        <TemplateDot $color={tpl.dot} />
                        {tpl.name}
                      </TemplateChip>
                    ))}
                  </TemplateChips>
                    <div style={{ fontSize: 12, color: colors.muted, marginBottom: 6 }}>Titel</div>
                    <DateInput
                      type="text"
                      value={newSluitTitle}
                      onChange={e => setNewSluitTitle(e.target.value.slice(0, 25))}
                      placeholder="Bijv. Zomervakantie"
                      autoFocus
                    />
                    <div style={{ fontSize: 11, color: newSluitTitle.length >= 25 ? '#ef4444' : colors.muted, textAlign: 'right', marginTop: 4 }}>{newSluitTitle.length}/25</div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, color: colors.muted, marginBottom: 6 }}>Bericht voor klanten</div>
                    <ReasonTextarea
                      value={newSluitReason}
                      onChange={e => setNewSluitReason(e.target.value.slice(0, 300))}
                      placeholder="Bijv. We zijn gesloten vanwege vakantie..."
                      style={{ minHeight: 120, marginBottom: 0 }}
                    />
                    <div style={{ fontSize: 11, color: newSluitReason.length >= 300 ? '#ef4444' : colors.muted, textAlign: 'right', marginTop: 4 }}>{newSluitReason.length}/300</div>
                  </div>
                  <div
                    onClick={() => setNewSluitCanCall(v => !v)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '12px 14px',
                      background: colors.background, border: `1px solid ${colors.border}`,
                      borderRadius: 10, cursor: 'pointer', userSelect: 'none',
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: colors.text }}>Klanten kunnen bellen om te bestellen</div>
                      <div style={{ fontSize: 11, color: colors.muted, marginTop: 2 }}>Toont telefoonnummer op de sluitingspagina</div>
                    </div>
                    <div style={{
                      width: 38, height: 22, borderRadius: 11, flexShrink: 0,
                      background: newSluitCanCall ? colors.brand : colors.border,
                      position: 'relative', transition: 'background 0.2s', marginLeft: 12,
                    }}>
                      <div style={{
                        position: 'absolute', top: 3, left: newSluitCanCall ? 19 : 3,
                        width: 16, height: 16, borderRadius: '50%', background: '#fff',
                        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      }} />
                    </div>
                  </div>
                </div>
              </div>

              <ModalActions style={{ marginTop: 24 }}>
                <SecondaryBtn onClick={() => setShowAddSluitingModal(false)}>Annuleren</SecondaryBtn>
                <PrimaryBtn onClick={saveSluiting} disabled={!newSluitTitle.trim() || !newSluitStartDate}>{editingSluiting ? 'Opslaan' : 'Toevoegen'}</PrimaryBtn>
              </ModalActions>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
      {/* Bevestiging verwijderen — eigen kleine overlay zonder body-lock */}
      {confirmDelete && (
        <div
          onClick={() => setConfirmDelete(null)}
          style={{
            position: 'fixed', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 99999, padding: 24,
          }}
        >
          <ModalContent onClick={e => e.stopPropagation()} style={{ maxWidth: 360 }}>
            <ModalHeader>
              <ModalTitle>Sluiting verwijderen?</ModalTitle>
              <CloseButton onClick={() => setConfirmDelete(null)}><X size={16} /></CloseButton>
            </ModalHeader>
            <ModalBody>
              <div style={{ fontSize: 15, color: colors.muted, marginBottom: 20, lineHeight: 1.5 }}>
                Weet je zeker dat je <strong style={{ color: colors.text }}>{confirmDelete.title || confirmDelete.reason}</strong> wilt verwijderen?
              </div>
              <ModalActions>
                <SecondaryBtn onClick={() => setConfirmDelete(null)}>Annuleren</SecondaryBtn>
                <PrimaryBtn style={{ background: '#ef4444' }} onClick={async () => { await deleteSluiting(confirmDelete.id); setConfirmDelete(null); }}>Verwijderen</PrimaryBtn>
              </ModalActions>
            </ModalBody>
          </ModalContent>
        </div>
      )}
    </Container>
  );
}
